import { WorkflowEditorService } from 'api-clients/monolith';
import { useEffect, useState } from 'react';

export interface RehireMetadataField {
  key: string;
  remoteKey: string | null;
}

export interface RehireMetadata {
  fields: RehireMetadataField[];
  observedValues: Array<{ value: string; count: number }>;
  sampleSize: number;
  syncState: {
    initialSyncCompleted: boolean;
    lastSyncedAt: string | null;
  };
}

const EMPTY_METADATA: RehireMetadata = {
  fields: [],
  observedValues: [],
  sampleSize: 0,
  syncState: { initialSyncCompleted: false, lastSyncedAt: null },
};

async function fetchMetadata(
  connectorUuid: string,
  eligibilityField?: string,
): Promise<RehireMetadata> {
  const response = eligibilityField
    ? await WorkflowEditorService.getInternalApiWorkflowEditorRehireCheckMetadata(
        connectorUuid,
        eligibilityField,
      )
    : await WorkflowEditorService.getInternalApiWorkflowEditorRehireCheckMetadata(
        connectorUuid,
      );
  return response.metadata;
}

export function useRehireMetadata(
  connectorUuid: string,
  eligibilityField: string,
): { metadata: RehireMetadata; loading: boolean } {
  const [metadata, setMetadata] = useState<RehireMetadata>(EMPTY_METADATA);
  const [loading, setLoading] = useState(Boolean(connectorUuid));

  useEffect(() => {
    let mounted = true;
    if (!connectorUuid) {
      setMetadata(EMPTY_METADATA);
      setLoading(false);
      return () => {
        mounted = false;
      };
    }

    setLoading(true);
    void fetchMetadata(connectorUuid)
      .then(async baseMetadata => {
        const field =
          eligibilityField ||
          (baseMetadata.fields.length === 1 ? baseMetadata.fields[0].key : '');
        return field ? fetchMetadata(connectorUuid, field) : baseMetadata;
      })
      .then(result => {
        if (mounted) setMetadata(result);
      })
      .catch(() => {
        if (mounted) setMetadata(EMPTY_METADATA);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [connectorUuid, eligibilityField]);

  return { metadata, loading };
}
